import { supabase }        from "@/lib/supabase";
import { supabaseCircuit } from "@/runtime/httpCircuitBreaker";

/* =========================================================
🧠 LEARNING ENGINE (ELITE v4 - RESILIENT + SCALABLE)
========================================================= */

type FixOutcome = "success" | "failure";

interface FixMemory {
  issueSignature: string;
  plans: {
    plan: any;
    hash: string;
    successCount: number;
    failureCount: number;
    lastUsed: number;
  }[];
}

/* =========================================================
MEMORY STORE (FAST LAYER)
========================================================= */

const memory = new Map<string, FixMemory>();

/* =========================================================
UTILS
========================================================= */

function normalize(text: string) {
  return (text || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

/* 🔥 FIXED (THIS WAS BREAKING YOUR BUILD) */
function createSignature(issue: {
  category?: string;
  title?: string;
  rootCause?: string;
}) {
  return `${issue?.category || "unknown"}::${normalize(
    issue?.title || ""
  )}::${normalize(issue?.rootCause || "")}`;
}

/* 🔥 FAST HASH (REPLACES JSON.stringify COST) */
function hashPlan(plan: any): string {
  try {
    return btoa(unescape(encodeURIComponent(JSON.stringify(plan)))).slice(0, 50);
  } catch {
    return Math.random().toString(36);
  }
}

/* =========================================================
CONFIDENCE SCORE (IMPROVED)
========================================================= */

function getScore(success: number, failure: number) {
  const total = success + failure;
  if (total === 0) return 0;

  const rate = success / total;

  // smoother scaling
  return rate * Math.log(total + 1.5);
}

/* =========================================================
🔥 LOAD FROM DB (SAFE + NON-BLOCKING)
========================================================= */

export async function loadLearningFromDB() {
  try {
    // CLASSIFICATION: fail-open - learning load failure must never block boot.
    // supabaseCircuit protects against cascading failures on Supabase unavailability.
    const rows = await supabaseCircuit.execute(
      async () => {
        const { data, error } = await supabase
          .from("ai_fix_memory")
          .select("*")
          .limit(500);
        if (error) throw new Error(error.message);
        return data ?? [];
      },
      { fallback: () => [] },
    );

    for (const row of rows || []) {
      if (!row?.issue_signature || !row?.plan) continue;

      const key = row.issue_signature;

      if (!memory.has(key)) {
        memory.set(key, {
          issueSignature: key,
          plans: [],
        });
      }

      memory.get(key)!.plans.push({
        plan: row.plan,
        hash: hashPlan(row.plan),
        successCount: row.success_count || 0,
        failureCount: row.failure_count || 0,
        lastUsed: new Date(row.last_used || Date.now()).getTime(),
      });
    }

  } catch { /* non-fatal: in-memory cache stays empty */ }
}

/* =========================================================
🔥 STORE RESULT (LOCAL FIRST + SAFE DB SYNC)
========================================================= */

export async function recordFixResult(
  issue: any,
  plan: any,
  outcome: FixOutcome
) {
  try {
    const key = createSignature(issue);
    const planHash = hashPlan(plan);

    /* ---------- LOCAL MEMORY ---------- */

    if (!memory.has(key)) {
      memory.set(key, {
        issueSignature: key,
        plans: [],
      });
    }

    const entry = memory.get(key)!;

    let existing = entry.plans.find((p) => p.hash === planHash);

    if (!existing) {
      existing = {
        plan,
        hash: planHash,
        successCount: 0,
        failureCount: 0,
        lastUsed: Date.now(),
      };

      entry.plans.push(existing);
    }

    existing.lastUsed = Date.now();

    if (outcome === "success") existing.successCount++;
    else existing.failureCount++;

    /* ---------- SORT + PRUNE ---------- */

    entry.plans = entry.plans
      .sort(
        (a, b) =>
          getScore(b.successCount, b.failureCount) -
          getScore(a.successCount, a.failureCount)
      )
      .slice(0, 5);

    /* ---------- DB SYNC (NON-BLOCKING, CIRCUIT-PROTECTED) ---------- */
    // CLASSIFICATION: fail-open - learning sync failure must never block healing cycles.
    // Circuit protection prevents thundering-herd retries when Supabase is unavailable.
    supabaseCircuit.execute(
      () => syncToDB(key, existing),
      { fallback: () => {} },
    ).catch((err) => {
      console.warn("⚠️ DB sync skipped:", err);
    });
  } catch (err) {
    console.warn("⚠️ Learning record failed:", err);
  }
}

/* =========================================================
🔥 ASYNC DB SYNC (ISOLATED - NEVER BREAKS APP)
========================================================= */

async function syncToDB(key: string, entry: any) {
  const { data: rows } = await supabase
    .from("ai_fix_memory")
    .select("*")
    .eq("issue_signature", key);

  const match = rows?.find(
    (r) => hashPlan(r.plan) === entry.hash
  );

  if (!match) {
    await supabase.from("ai_fix_memory").insert({
      issue_signature: key,
      plan: entry.plan,
      success_count: entry.successCount,
      failure_count: entry.failureCount,
      score: getScore(entry.successCount, entry.failureCount),
      last_used: new Date(),
    });
  } else {
    const newSuccess = match.success_count + entry.successCount;
    const newFailure = match.failure_count + entry.failureCount;

    await supabase
      .from("ai_fix_memory")
      .update({
        success_count: newSuccess,
        failure_count: newFailure,
        score: getScore(newSuccess, newFailure),
        last_used: new Date(),
      })
      .eq("id", match.id);
  }
}

/* =========================================================
RETRIEVE BEST FIX (SMART THRESHOLD)
========================================================= */

export function getLearnedFix(issue: any) {
  try {
    const key = createSignature(issue);
    const entry = memory.get(key);

    if (!entry || entry.plans.length === 0) return null;

    const best = entry.plans.sort(
      (a, b) =>
        getScore(b.successCount, b.failureCount) -
        getScore(a.successCount, a.failureCount)
    )[0];

    const score = getScore(best.successCount, best.failureCount);

    /* 🔥 IMPROVED CONFIDENCE THRESHOLD */
    if (score < 0.4) return null;

    return best.plan;
  } catch {
    return null;
  }
}

/* =========================================================
CLEANUP (AGING + MEMORY CONTROL)
========================================================= */

export function cleanupLearning(maxAgeMs = 1000 * 60 * 60 * 24) {
  const now = Date.now();

  for (const [key, entry] of memory.entries()) {
    entry.plans = entry.plans.filter(
      (p) => now - p.lastUsed < maxAgeMs
    );

    if (entry.plans.length === 0) {
      memory.delete(key);
    }
  }
}

/* =========================================================
DEBUG / OBSERVABILITY
========================================================= */

export function getLearningStats() {
  return {
    totalIssues: memory.size,
    entries: Array.from(memory.values()),
  };
}